import { ModaSofa } from '@dora/backpack';
import { finishesEqual, FinishT } from '@dora/backpack/src/configurableProperties';
import { createSelector } from '@reduxjs/toolkit';
import * as E from 'fp-ts/Either';
import { identity, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { FurnitureDescription } from '../../data/furnitureDescription';
import * as Pr from '../../data/product';
import { Product } from '../../data/product';
import { RootState } from '../../store';
import { CatalogueError } from '../catalogue';
import * as ConfigurablePropertySelectors from './configurableProperties';
import * as Core from './core';
import { templates } from './modaSofa';
import * as ProductSelectors from './productData';

/**
 * Raw catalogue store data
 */
const catalogueEntries = (state: RootState) => state.catalogue;

export const activeCatalogueEntries = createSelector(
    catalogueEntries,
    ProductSelectors.discontinuedProducts,
    (entries, discontinuedProducts) => {
        return entries.filter(
            e =>
                !e.furnitureDescription.variants.some(v =>
                    discontinuedProducts?.some(p => v.canonical_id === p.sku),
                ),
        );
    },
);

/**
 * Get a Record of FurnitureDescriptions for simple furnitures only,
 * keyed on SKU.
 *
 * Simple furnitures are those which, in contrast to configurable furnitures
 * (which need a full configuration space) and moda modular sofas (which require
 * templates), consist of one single-article product.
 */
export const simpleFurnitureDescriptionBySku = createSelector(activeCatalogueEntries, catalogue =>
    catalogue.reduce((record, entry) => {
        if (entry.furnitureDescription.variants.length === 1) {
            record[entry.furnitureDescription.variants[0].canonical_id] =
                entry.furnitureDescription;
        }
        return record;
    }, {} as Record<string, FurnitureDescription>),
);

const isSkuInTemplates = (
    templates: ModaSofa.ResolvedCurvedTemplate | ModaSofa.ResolvedStandardTemplate,
    sku: string,
) => {
    const middleSku = templates.kind === 'curved' ? templates.middle.sku : templates.straight.sku;
    return (
        (templates.kind === 'standard' &&
            Object.values(templates.corner).some(template => template?.sku === sku)) ||
        middleSku === sku ||
        // end piece SKUs in templates are currently longer than actual skus (as a hack, they have
        // -Right and -Left appended)
        templates.end.left.sku.includes(sku) ||
        templates.end.right.sku.includes(sku)
    );
};

/**
 * Get catalogue entries filtered to only include the current range.
 * Includes individual modular sofa pieces.
 */
export const catalogueEntriesForCurrentRange = createSelector(
    activeCatalogueEntries,
    Core.range,
    (catalogueEntries, range) =>
        pipe(
            E.Do,
            E.bind('entries', () =>
                pipe(
                    catalogueEntries,
                    E.fromPredicate(
                        e => e.length > 0,
                        () => CatalogueError.CATALOGUE_NOT_INITIALISED,
                    ),
                ),
            ),
            E.bind('range', () =>
                pipe(range, E.fromNullable(CatalogueError.RANGE_NOT_INITIALISED)),
            ),
            E.map(({ range, entries }) => entries.filter(data => data.ranges.includes(range))),
        ),
);

/**
 * All products which are part of the current range, including anything that makes up catalogue
 * entry furniture, as well as any individual modular sofa pieces.
 */
export const productsInCatalogueEntriesForCurrentRange = createSelector(
    catalogueEntriesForCurrentRange,
    ProductSelectors.products,
    (catalogueEntries, products) =>
        pipe(
            E.Do,
            E.bind('catalogueEntries', () => pipe(catalogueEntries)),
            E.bind('products', () =>
                pipe(products, E.fromNullable(CatalogueError.PRODUCTS_NOT_INITIALISED)),
            ),
            E.map(({ catalogueEntries, products }) => {
                const allProductSkusInCatalogue = catalogueEntries.flatMap(ce =>
                    ce.furnitureDescription.variants.map(v => v.canonical_id),
                );

                return allProductSkusInCatalogue
                    .map(sku => products.find(p => Pr.productId(p) === sku))
                    .filter(p => p !== undefined) as Product[];
            }),
        ),
);

/**
 * Get catalogue entries filtered to only include the current range.
 * Also filters out individual modular sofa pieces.
 */
export const catalogueEntriesForCurrentRangeWithoutModularSofaPieces = createSelector(
    catalogueEntriesForCurrentRange,
    templates,
    (catalogueEntries, sofaTemplates) =>
        pipe(
            E.Do,
            E.bind('catalogueEntries', () => pipe(catalogueEntries)),
            E.bind('sofaTemplates', () => E.right(O.fromNullable(sofaTemplates))),
            E.map(({ catalogueEntries, sofaTemplates }) =>
                catalogueEntries.filter(data =>
                    pipe(
                        sofaTemplates,
                        O.map(
                            templates =>
                                // sofa pieces can appear in multiple ranges so we need to filter out pieces that
                                // appear in any template, not just the current range
                                !Object.values(templates).some(template =>
                                    data.furnitureDescription.variants.some(product =>
                                        isSkuInTemplates(template, product.canonical_id),
                                    ),
                                ),
                        ),
                        O.getOrElse<boolean>(() => true),
                    ),
                ),
            ),
        ),
);

/**
 * Get catalogue entries filtered by current configurable properties
 */
export const catalogueEntriesForGlobalPropertySelections = createSelector(
    catalogueEntriesForCurrentRangeWithoutModularSofaPieces,
    ConfigurablePropertySelectors.currentPropertySelections,
    ProductSelectors.products,
    (catalogueEntries, configurablePropertySelections, products) => {
        return pipe(
            E.Do,
            E.bind('catalogueEntries', () => pipe(catalogueEntries)),
            E.bind('currentConfigurableProperties', () =>
                pipe(
                    configurablePropertySelections,
                    E.fromNullable(CatalogueError.CONFIGURABLE_PROPERTIES_NOT_INITIALISED),
                ),
            ),
            E.bind('products', () =>
                pipe(products, E.fromNullable(CatalogueError.PRODUCTS_NOT_INITIALISED)),
            ),
            E.map(({ catalogueEntries, currentConfigurableProperties, products }) =>
                catalogueEntries.filter(data => {
                    const skus = data.furnitureDescription.variants.map(v => v.canonical_id);
                    const productsFromSkus = products.filter(p => skus.includes(Pr.productId(p)));

                    // find any variant within this furniture that has configurable properties
                    const configurablePropertiesForEntry = productsFromSkus.filter(
                        prop =>
                            prop.properties.rattan_colour !== undefined ||
                            prop.properties.table_finish !== undefined,
                    );

                    // ensure the properties on variants within the furniture all align with the properties in state
                    return configurablePropertiesForEntry.every(configurableProperty => {
                        return pipe(
                            O.fromNullable(configurableProperty?.properties),
                            O.map(configurablePropertiesForEntry => {
                                // Find the properties on the entry that are configurable according to our state
                                const propertiesInCurrentConfigurable =
                                    currentConfigurableProperties.filter(
                                        prop => !!configurablePropertiesForEntry[prop.propertyKey],
                                    );
                                // Ensure that every configurable property value on the entry matches the current property in state
                                return propertiesInCurrentConfigurable.every(prop => {
                                    const propOnEntry = configurablePropertiesForEntry[
                                        prop.propertyKey
                                    ] as FinishT;
                                    return propOnEntry
                                        ? finishesEqual(prop.finish, propOnEntry)
                                        : false;
                                });
                            }),
                            O.fold(() => false, identity),
                        );
                    });
                }),
            ),
        );
    },
);
